import { ChangeDetectionStrategy, Component, input, model } from '@angular/core';
import { scrollToInvalidControl } from '@app/v2/directives/scroll-to-invalid-control';
import { disableFormEffect } from '@app/v2/utils/signal-utils';
import { CardMode } from '@components/card/card.component';
import { PersistentFormGroup } from '@utility/persistent-forms';
// I tried mixins to separate formGroup from view-only behavior, but Angular doesn't support them well (inputs dont work)
// I tried also hostDirectives but they cannot be generic :< so here we are, a base class that card components can extend to get formGroup behavior.
// It's not ideal but it works and is fairly straightforward
@Component({
    imports: [],
    template: '',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export abstract class BaseCardFormComponent<TForm extends PersistentFormGroup<any>, TModel> {
    isLoading = model.required<boolean>();
    showEditSaveButton = input.required<boolean>();
    mode = model.required<CardMode>();
    formGroup = input.required<TForm | null>();
    model = input.required<TModel | null | undefined>(); // undefined - not yet assigned, null - assigned as empty or no data,
    config: ReturnType<BaseCardFormComponent<TForm, TModel>['configCardForm']> = this.configCardForm({});

    configCardForm(opts: {
        onSave?: (setModeFn: (mode: CardMode) => void) => void;
        onEdit?: (setModeFn: (mode: CardMode) => void) => void;
        onCancel?: (setModeFn: (mode: CardMode) => void) => void;
    }) {
        const onSave =
            opts.onSave ??
            ((setModeFn) => {
                const formGroup = this.formGroup();
                if (formGroup === null) return;
                formGroup.updateValueAndValidity();
                if (formGroup.invalid) {
                    console.log('form is invalid, not changing mode', formGroup);
                    formGroup.markAllAsTouched();
                    // Scroll to the first invalid control
                    scrollToInvalidControl(formGroup);
                    return;
                }
                setModeFn('view');
            });

        const onEdit =
            opts.onEdit ??
            ((setModeFn) => {
                setModeFn('edit');
            });

        const onCancel =
            opts.onCancel ??
            ((setModeFn) => {
                setModeFn('view');
            });

        return {
            onSave,
            onEdit,
            onCancel,
        };
    }

    disableForm = disableFormEffect({
        form: () => this.formGroup(),
        disabled: () => this.mode() === 'view',
    });

    changeModeFn = (mode: CardMode, setModeFn: (mode: CardMode) => void, cancel?: boolean) => {
        if (!this.config) return;
        if (cancel) {
            return this.config.onCancel(setModeFn);
        } else if (mode === 'edit') {
            return this.config.onEdit(setModeFn);
        } else {
            // Do not gate Save on formGroup.dirty. Angular can leave real changes pristine
            // after FormArray mutations, reset(), or disabled-control flows.
            return this.config.onSave(setModeFn);
        }
    };
}
